import {useCallback, useEffect, useState, useMemo} from 'react'
import {useSession} from '~/auth/AuthProvider'
import useSnackbar from '~/components/snackbar/useSnackbar'

import {
  SoftwareIndicatorAttributeLevel,
  SoftwareEvaluationResult,
  saveSoftwareQualityAttributeLevelEvaluation,
  SoftwareQualityEvaluator,
  getAllEvaluatedSoftwareEntries,
  checkIfIsHelmholtzSoftware,
  saveSoftwareQualityEvaluation,
  getSoftwareQualityEvaluations,
  SoftwareEvaluationData,
  SoftwareEvaluationStatus,
  SoftwareDimensionScore,
  SoftwareEvaluationArtefact,
  saveSoftwareEvaluationArtefact,
  EvaluationData,
  getSoftwareQualityEvaluationData,
  SoftwareIndicatorDimension,
  SoftwareIndicatorAttribute,
  SoftwareAttributeScore,
  SoftwareIndicatorReviewStatus,
  SoftwareQualityAttributeLevelEvaluation,
  ActivityLogRow,
  SoftwareEvaluationReportingInformation,
  getAllSoftwareQualityAttributeLevelEvaluations,
  submitEvaluation,
  getReleasesForSoftwareEvaluation,
  saveSoftwareEvaluationPoFTopic,
  ContactPersonTableSchema,
  saveSoftwareEvaluationContactPerson,
  getContributorsForSoftwareEvaluation} from './apiSoftwareIndicator'
import {useSoftwareIndicatorConfiguration} from './useSoftwareIndicatorConfiguration'
import {getCategoryForSoftwareIds} from '../apiSoftware'
import {ArtefactOption} from './SoftwareEvaluationArtefact'
import {EvaluationContactPersonOption} from './SoftwareEvaluationPoFTopic'
import {getMaintainersOfSoftware} from '../edit/maintainers/apiSoftwareMaintainers'
import {RawMaintainerProps, rawMaintainersToMaintainers} from '~/components/maintainers/apiMaintainers'


export function useSoftwareEvaluation({software, year = ''}: {software: string, year?: string}) {
  const {token, user} = useSession()
  const {showWarningMessage} = useSnackbar()
  const [loading, setLoading] = useState(true)

  const [evaluationData, setEvaluationData] = useState<EvaluationData | null>()

  const [evaluatedSoftware, setEvaluatedSoftware] = useState<{software_id: string, software_slug: string, software_name: string}[]>([])
  const [isSoftwareOfTenantOrganisation, setIsSoftwareOfTenantOrganisation] = useState<boolean>(false)
  const [softwareEvaluations, setSoftwareEvaluations] = useState<SoftwareEvaluationData[]>([])
  const [currentSoftwareEvaluation, setCurrentSoftwareEvaluation] = useState<SoftwareEvaluationData | undefined>(undefined)
  const [categoriesOfSoftware, setCategoriesOfSoftware] = useState<Set<string>>(new Set<string>())
  const [softwareArtefactOptions, setSoftwareArtefactOptions] = useState<ArtefactOption[]>([])
  const [softwareContactPersonOptions, setSoftwareContactPersonOptions] = useState<EvaluationContactPersonOption[]>([])

  useEffect(() => {
    fetchEvaluationData()
    getEvaluatedSoftware()
    checkIfIsSoftwareOfTenantOrganisation()
    getSoftwareEvaluations()
    getCategoriesOfSoftware()
    getVersionsOfSoftware()
    getPersonsAssociatedWithSoftware()
  }, [software, year])

  const configurationHook = useSoftwareIndicatorConfiguration(year)

  const fetchEvaluationData = useCallback(async () => {
    setLoading(true)
    if (software && year) {
      const data = await getSoftwareQualityEvaluationData({software_id: software, year, token})
      setEvaluationData(data)
    }
    setLoading(false)
  }, [software, year])

  const statusHistory: ActivityLogRow[] | null = useMemo(() => evaluationData?.status_history ?? null, [evaluationData?.status_history])
  const currentStatus: SoftwareEvaluationStatus | null = statusHistory ? statusHistory[0] : null
  const currentAttributeLevelEvaluations: SoftwareQualityAttributeLevelEvaluation[] | null = useMemo(() => evaluationData?.current_level_evaluations ?? null, [evaluationData?.current_level_evaluations])
  const currentArtefact: SoftwareEvaluationArtefact | null = useMemo(() => evaluationData?.current_artefact ?? null, [evaluationData?.current_artefact])
  const currentReportingInformation: SoftwareEvaluationReportingInformation[] | null = useMemo(() => evaluationData?.current_reporting_information ?? null, [evaluationData?.current_reporting_information])


  const getLevelEvaluationsByDimensionId = useCallback((dimensionId: SoftwareIndicatorDimension['id']): SoftwareQualityAttributeLevelEvaluation[] | null => {
    return currentAttributeLevelEvaluations?.filter(evaluation => evaluation.dimension_id == dimensionId) ?? null
  }, [currentAttributeLevelEvaluations])

  const getLevelEvaluationsByAttributeId = useCallback((attributeId: SoftwareIndicatorAttribute['id']): SoftwareQualityAttributeLevelEvaluation[] | null => {
    return currentAttributeLevelEvaluations?.filter(evaluation => evaluation.attribute_id == attributeId) ?? null
  }, [currentAttributeLevelEvaluations])

  const getLevelEvaluationByAttributeLevelId = useCallback((attributeLevelId: SoftwareIndicatorAttributeLevel['id']): SoftwareQualityAttributeLevelEvaluation | null => {
    return currentAttributeLevelEvaluations?.find(evaluation => evaluation.attribute_level_id == attributeLevelId) ?? null
  }, [currentAttributeLevelEvaluations])

  const getDimensionEvaluationProgress = useCallback((dimensionId: SoftwareIndicatorDimension['id']): number => {
    const evaluations = getLevelEvaluationsByDimensionId(dimensionId)
    const levels = configurationHook.getIndicatorAttributeLevels({dimensionId: dimensionId}).filter(level => level.level != 0)
    return ((evaluations?.filter(evaluation => (evaluation.fulfilled !== null)).length ?? 0) / levels.length) * 100
  }, [currentAttributeLevelEvaluations])

  const getDimensionScore = useCallback((dimensionId: SoftwareIndicatorDimension['id']): SoftwareDimensionScore['dimension_score'] | null => {
    console.log(`evaluationData for diemnsionScore |${!evaluationData}|`)
    if (!evaluationData) return null
    return evaluationData.evaluation_status.find(dimension => dimension.id === dimensionId)?.dimension_score ?? null
  }, [evaluationData?.evaluation_status])

  const getAttributeScore = useCallback((attributeId: SoftwareIndicatorAttribute['id']): SoftwareAttributeScore['attribute_score'] | null => {
    if (!evaluationData) return null
    const allAttributeScores = evaluationData.evaluation_status.flatMap(d => d.attributes)
    return allAttributeScores.find(attr => attr.id === attributeId)?.attribute_score ?? null
  }, [evaluationData?.evaluation_status])

  const getDimensionReviewStatus = useCallback((dimensionId: SoftwareIndicatorDimension['id']): SoftwareIndicatorReviewStatus | null => {
    if (!evaluationData) return null
    return evaluationData.evaluation_status.find(dimension => dimension.id === dimensionId)?.dimension_status ?? null
  }, [evaluationData?.evaluation_status])

  const getAttributeReviewStatus = useCallback((attributeId: SoftwareIndicatorDimension['id']): SoftwareIndicatorReviewStatus | null => {
    if (!evaluationData) return null
    const allAttributeScores = evaluationData.evaluation_status.flatMap(d => d.attributes)
    return allAttributeScores.find(attr => attr.id === attributeId)?.attribute_status ?? null
  }, [evaluationData?.evaluation_status])

  const getAttributeLastUpdatedDate = useCallback((attributeId: SoftwareIndicatorAttribute['id']): Date | null => {
    const attributeData = getLevelEvaluationsByAttributeId(attributeId)
    if (!attributeData?.length) {
      return null
    }
    const mostRecent = attributeData.reduce((recent, current) => {
      const recentDate = new Date(recent.evaluated_at)
      const currentDate = new Date(current.evaluated_at)

      return currentDate > recentDate ? current : recent
    })

    return new Date(mostRecent.evaluated_at)
  }, [currentAttributeLevelEvaluations])

  const getDimensionLastUpdatedDate = useCallback((dimensionId: SoftwareIndicatorDimension['id']): Date | null => {
    const dimensionData = getLevelEvaluationsByDimensionId(dimensionId)
    if (!dimensionData?.length) {
      return null
    }

    const mostRecent = dimensionData.reduce((recent, current) => {
      const recentDate = new Date(recent.evaluated_at)
      const currentDate = new Date(current.evaluated_at)

      return currentDate > recentDate ? current : recent
    })

    return new Date(mostRecent.evaluated_at)
  }, [currentAttributeLevelEvaluations])

  const attributeEvaluators = useCallback((attributeId: SoftwareIndicatorAttribute['id']): string[] => {
    const attributeData = getLevelEvaluationsByAttributeId(attributeId)
    if (!attributeData) {
      return []
    }
    const evaluators: SoftwareQualityEvaluator[] = attributeData.map(evaluation => evaluation.evaluator)
    const uniqueEvaluators = evaluators.filter((obj, index, self) => index === self.findIndex((t) => t.id === obj.id))
    return uniqueEvaluators.map(evaluator => evaluator.given_names + ' ' + evaluator.family_names)
  }, [currentAttributeLevelEvaluations])

  const dimensionEvaluators = useCallback((dimensionId: SoftwareIndicatorDimension['id']): string[] => {
    const dimensionData = getLevelEvaluationsByDimensionId(dimensionId)
    if (!dimensionData) {
      return []
    }
    const evaluators: SoftwareQualityEvaluator[] = dimensionData.map(evaluation => evaluation.evaluator)
    const uniqueEvaluators = evaluators.filter((obj, index, self) => index === self.findIndex((t) => t.id === obj.id))
    return uniqueEvaluators.map(evaluator => evaluator.given_names + ' ' + evaluator.family_names)
  }, [currentAttributeLevelEvaluations])

  const softwareEvaluators = useCallback((): SoftwareQualityEvaluator[] => {
    if (!currentAttributeLevelEvaluations) {
      return []
    }
    const evaluators: SoftwareQualityEvaluator[] = currentAttributeLevelEvaluations.map(evaluation => evaluation.evaluator)
    const uniqueEvaluators = evaluators.filter((obj, index, self) => index === self.findIndex((t) => t.id === obj.id))
    return uniqueEvaluators
  }, [currentAttributeLevelEvaluations])

  const evaluationResult: SoftwareEvaluationResult | null = useMemo(() => evaluationData?.evaluation_result, [evaluationData?.evaluation_result]) ?? null

  // fix: replace with information coming from database
  const allQuestionsAnswered = useMemo((): boolean => {
    const yesNoAnswers = currentAttributeLevelEvaluations?.filter(evaluation => evaluation.fulfilled !== null)
    return yesNoAnswers?.length === configurationHook.getIndicatorAttributeLevels().filter(level => level.level !== 0).length
  }, [currentAttributeLevelEvaluations])

  //check if at least one centre has both PoF topic and contact person
  const reportingInformationFulfilled = useMemo((): boolean => {
    const centreReportingInformationAvailable = currentReportingInformation?.map(entry => Boolean(entry.pof_topic.id && entry.contact_person.name)) ?? []
    return centreReportingInformationAvailable.some(centre => centre === true)
  }, [currentReportingInformation])

  const addAttributeLevelEvaluation = useCallback(async (
    attribute_levelId: SoftwareIndicatorAttributeLevel['id'],
    fulfilled: boolean | null,
  ) => {
    setLoading(true)
    if (software && user && year) {
      if (currentSoftwareEvaluation) {
        const levelEvaluationData = {
          software,
          evaluation_id: currentSoftwareEvaluation.id,
          attribute_level: attribute_levelId,
          fulfilled,
          evaluator: user.account
        }
        updateSubmissionResetSnackbar()
        await saveSoftwareQualityAttributeLevelEvaluation({levelEvaluation: levelEvaluationData, token})
        await fetchEvaluationData()
        await getSoftwareEvaluations()
      }
    }
    setLoading(false)
  }, [token, softwareEvaluations, currentSoftwareEvaluation, software, user, year])

  const getEvaluatedSoftware = useCallback(async () => {
    setLoading(true)
    const softwareEntries = await getAllEvaluatedSoftwareEntries({token})
    setEvaluatedSoftware(softwareEntries)
    setLoading(false)
  }, [token])

  const softwareScorePolygon = useMemo(() => {
    const polygon = configurationHook.minimalPolygon.map((dimension) => {
      const match = getDimensionScore(dimension.id)
      return {...dimension, minimum_required_level: (match !== null && match !== undefined) ? Number(match.toFixed(1)) : 0} //match.level_score
    })
    return polygon
  }, [configurationHook.minimalPolygon, software, year, evaluationData?.current_level_evaluations])

  const softwareQualityDimensionStatus = useMemo(() => {
    if (software && softwareScorePolygon) {
      return Object.fromEntries(configurationHook.minimalPolygon.map(requiredValue => [
        requiredValue.name,
        (softwareScorePolygon.find(scoreValue => scoreValue.name === requiredValue.name)?.minimum_required_level ?? 0) - requiredValue.minimum_required_level
      ]))
    }
    return {}
  }, [software, configurationHook.minimalPolygon, softwareScorePolygon, year])

  const checkIfIsSoftwareOfTenantOrganisation = useCallback(async () => {
    setLoading(true)
    if (software) {
      const check = await checkIfIsHelmholtzSoftware({software, token})
      setIsSoftwareOfTenantOrganisation(check)
    }
    setLoading(false)
  }, [token, software])

  const getSoftwareEvaluations = useCallback(async () => {
    setLoading(true)
    if (software) {
      const evaluations = await getSoftwareQualityEvaluations({software: [software], token})
      setSoftwareEvaluations(evaluations)
      setCurrentSoftwareEvaluation(evaluations.find(evl => evl.year === year))
    }
    setLoading(false)
  }, [token, software, year])

  const saveArtefact = useCallback(async (artefact: string | null) => {
    setLoading(true)
    if (user && evaluationData) {
      const artefactData = {
        evaluation_id: evaluationData.id,
        artefact: artefact ?? null,
        evaluator: user.account,
        software: software
      }
      updateSubmissionResetSnackbar()
      await saveSoftwareEvaluationArtefact({artefact: artefactData, token})
      await fetchEvaluationData()
      await getSoftwareEvaluations()
      await fetchEvaluationData()
    }
    setLoading(false)
  }, [software, evaluationData?.id, currentSoftwareEvaluation?.submitted, user, token])

  const savePoFTopic = useCallback(async (organisation_id: string, category_id: string) => {
    setLoading(true)
    if (user && evaluationData && currentReportingInformation) {
      const topicData = {
        software: software,
        evaluation_id: evaluationData?.id,
        pof_topic: category_id,
        organisation: organisation_id,
        changed_by: user.account
      }
      updateSubmissionResetSnackbar()
      await saveSoftwareEvaluationPoFTopic({data: topicData, token})
      await getSoftwareEvaluations()
      await fetchEvaluationData()
    }
    setLoading(false)
  }, [software, currentReportingInformation, user, token])

  const saveContactPerson = useCallback(async (organisation_id: string, contactPerson: string | null, account?: string) => {
    setLoading(true)
    if (user && evaluationData && currentReportingInformation) {
      const topicData: ContactPersonTableSchema = {
        software: software,
        evaluation_id: evaluationData?.id,
        contact_person: contactPerson,
        contact_person_id: account,
        organisation: organisation_id,
        changed_by: user.account
      }
      updateSubmissionResetSnackbar()
      await saveSoftwareEvaluationContactPerson({data: topicData, token})
      await getSoftwareEvaluations()
      await fetchEvaluationData()
    }
    setLoading(false)

  }, [software, currentReportingInformation, user, token])

  const getCategoriesOfSoftware = useCallback(async () => {
    setLoading(true)
    const categories = await getCategoryForSoftwareIds(software, token)
    setCategoriesOfSoftware(categories)
    setLoading(false)
  }, [token])

  const getVersionsOfSoftware = useCallback(async () => {
    setLoading(true)
    if (software && year) {
      const releases: ArtefactOption[] | null = await getReleasesForSoftwareEvaluation(software, year, token)
      const doi_options: ArtefactOption[] = releases?.filter((release) => release.doi != null).map((release) => {
        return {...release, type: 'DOI', label: (release.version ? `Version  ${release.version} ` : '') + `(${release.doi})`}
      }) ?? []
      setSoftwareArtefactOptions(doi_options)
    }
    setLoading(false)
  }, [software, year, token])

  const getPersonsAssociatedWithSoftware = useCallback(async () => {
    setLoading(true)
    if (software && year) {
      const contributors: EvaluationContactPersonOption[] = await getContributorsForSoftwareEvaluation(software, token)
      const raw_maintainer_data: RawMaintainerProps[] = await getMaintainersOfSoftware({software, token})
      const maintainer_data = rawMaintainersToMaintainers(raw_maintainer_data)
      const maintainers: EvaluationContactPersonOption[] = maintainer_data.map((row) => {return {type: 'Maintainer', label: String(row.name), id: row.account}})
      setSoftwareContactPersonOptions([...maintainers, ...contributors])
    }
    setLoading(false)
  }, [software, year, token])

  const addSoftwareEvaluation = useCallback(async (
    software: string,
    year: string,
  ) => {
    setLoading(true)
    if (software && year) {
      const evaluationData = {
        software,
        year,
      }
      await saveSoftwareQualityEvaluation({evaluation: evaluationData, token})
    }
    setLoading(false)
  }, [token])

  const submitSoftwareEvaluation = useCallback(async () => {
    setLoading(true)
    if (currentSoftwareEvaluation && user) {
      await submitEvaluation({evaluation: {software_id: software, evaluation_year: year}, token})
      await getSoftwareEvaluations()
      await fetchEvaluationData()
    }
    setLoading(false)
  }, [token, softwareEvaluations, currentSoftwareEvaluation, currentArtefact])

  const loadAttributeLevelEvaluationHistory = useCallback(async (
    attribute_id: number
  ): Promise<SoftwareQualityAttributeLevelEvaluation[] | undefined> => {
    setLoading(true)
    if (software && year && user) {
      const data = await getAllSoftwareQualityAttributeLevelEvaluations({software, year, attribute_id, token})
      return data
    }
    setLoading(false)
  }, [software, year, currentAttributeLevelEvaluations])

  function updateSubmissionResetSnackbar() {
    if (currentSoftwareEvaluation?.submitted) {
      showWarningMessage('The submission was reset due to changes. Remember to re-submit in order for your evaluation to be considered.')
    }
  }

  return {
    ...configurationHook,
    loading,
    evaluationData,
    statusHistory,
    currentStatus,
    evaluationResult,
    getDimensionScore,
    getAttributeScore,
    getDimensionReviewStatus,
    getAttributeReviewStatus,
    currentAttributeLevelEvaluations,
    getLevelEvaluationsByDimensionId,
    getLevelEvaluationsByAttributeId,
    getLevelEvaluationByAttributeLevelId,
    getDimensionEvaluationProgress,
    getAttributeLastUpdatedDate,
    getDimensionLastUpdatedDate,
    attributeEvaluators,
    dimensionEvaluators,
    softwareEvaluators,

    softwareScorePolygon,

    evaluatedSoftware,
    softwareQualityDimensionStatus, //fix - replace with function to get diff for one dimension, isntead of for all?
    addAttributeLevelEvaluation,
    isSoftwareOfTenantOrganisation,
    addSoftwareEvaluation,
    submitSoftwareEvaluation,
    softwareEvaluations,
    currentSoftwareEvaluation,
    currentArtefact,
    saveArtefact,
    currentReportingInformation,
    savePoFTopic,
    saveContactPerson,
    loadAttributeLevelEvaluationHistory,
    allQuestionsAnswered,
    reportingInformationFulfilled,
    categoriesOfSoftware,
    softwareArtefactOptions,
    softwareContactPersonOptions
  }
}
